import { InputSelect, InputTextArea } from '@farmshare/ui-components';
import { getMeasurementLabel, RecursivePartial } from '@farmshare/utils';
import { Alert, Box, CardHeader, Typography } from '@mui/material';
import { FormikProps } from 'formik';
import { map } from 'lodash';
import { useEffect, useMemo } from 'react';
import { Col, Row } from 'react-bootstrap';

import {
  ProcessorCapability,
  ProcessorCapabilitySpecificationSpecifications,
} from 'lib/graphql';

import { useCutsheetProcessorCapabilities } from './helpers';
import { CutsheetFormData } from './types';

interface CutsheetSpecificationsProps {
  fP: FormikProps<CutsheetFormData>;
  capabilities: RecursivePartial<ProcessorCapability>[];
  readOnly?: boolean;
}

export function CutsheetSpecificationsSection({
  fP,
  capabilities,
  readOnly = false,
}: CutsheetSpecificationsProps) {
  const processorCapabilities = useCutsheetProcessorCapabilities(
    fP,
    capabilities,
  );
  const activeSpecification = useMemo(
    () => processorCapabilities?.activeSpecification,
    [processorCapabilities],
  );

  useEffect(() => {
    // Get the current Values
    const curr = { ...fP?.values?.selectedSpecifications };

    // Calculate defaults based on the active specification
    const defaults = activeSpecification?.specifications?.reduce(
      (acc, spec) => {
        if (!spec) return acc;
        const { name, options } =
          spec as ProcessorCapabilitySpecificationSpecifications;

        const defaultOption = options?.find((op) => op?.isDefault);
        if (defaultOption) {
          acc[name] = defaultOption.name;
          return acc;
        }

        return acc;
      },
      {} as Record<string, string | undefined>,
    );

    // Set the selectedSpecifications field with the defaults and current values
    fP.setFieldValue(`selectedSpecifications`, {
      ...defaults,
      ...curr,
    });
    fP.setFieldValue('activeSpecification', activeSpecification?._id);
    // TODO: Examine if fP should be in the dependency array
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSpecification]);

  const errorMessage = useMemo(() => {
    const isAnimalSpeciesValid =
      fP.values.animalSpecies &&
      String(fP.values.animalSpecies) !== 'Select one...';
    const isInspectionLevelValid =
      fP.values.inspectionLevel &&
      String(fP.values.inspectionLevel) !== 'Select one...';

    if (
      !activeSpecification ||
      !isAnimalSpeciesValid ||
      !isInspectionLevelValid
    ) {
      if (!isAnimalSpeciesValid && !isInspectionLevelValid) {
        return 'Please select both an animal species and an inspection level to display the specifications.';
      }
      if (!isAnimalSpeciesValid) {
        return 'Please select an animal species to display the specifications.';
      }
      if (!isInspectionLevelValid) {
        return 'Please select an inspection level to display the specifications.';
      }
    }

    return ''; // Return an empty string when there's no error
  }, [fP.values.animalSpecies, fP.values.inspectionLevel, activeSpecification]);

  return (
    <>
      <CardHeader title="Specifications" />
      {errorMessage && (
        <Alert severity="warning" variant="outlined">
          {errorMessage}
        </Alert>
      )}
      <Row xs={1} sm={2} md={3} lg={4} className="g-2">
        {activeSpecification?.specifications
          ?.filter((sp) => sp?.isVisible)
          .map((sp) => (
            <Col key={sp?._id as unknown as string}>
              <InputSelect
                autoSelectFirstOption={false}
                disabled={readOnly}
                label={`${sp?.name} ${
                  sp?.measurementUnit && sp?.measurementUnit !== 'none'
                    ? `(${getMeasurementLabel(sp?.measurementUnit)})`
                    : ''
                }`}
                options={map(
                  sp?.options?.filter((op) => op?.isVisible) ?? [],
                  (value) => ({
                    label: `${value?.name} ${getMeasurementLabel(
                      sp?.measurementUnit,
                    )}`,
                    value: value?.name || '',
                  }),
                )}
                nameOverride={`selectedSpecifications.${sp?.name}`}
                floatingLabel
                required={sp?.isRequired}
                className={sp?.name?.replace(/[\s\p{P}]/gu, '')}
              />
              <Box sx={{ mt: 1.5 }}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 0.5, fontSize: '0.75rem' }}
                >
                  Notes for {sp?.name}
                </Typography>
                <InputTextArea
                  nameOverride={`notes.${sp?.name}`}
                  disabled={readOnly}
                  label="Notes"
                  rows={2}
                  showLabel={false}
                />
              </Box>
            </Col>
          ))}
      </Row>
    </>
  );
}
